import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { WORDMARK } from '../../data/registration';
export interface PhotoCaption {
  eyebrow: string;
  line: string;
  names?: string[];
  stat?: {
    value: string;
    label: string;
  };
  footnote?: string;
}
interface PhotoSideProps {
  image: {
    src: string;
    alt: string;
  };
  caption: PhotoCaption;
}

/**
 * The archive side of the flow. A single photograph fills the column and
 * changes as the member moves; the caption beneath it escalates from the place
 * to the people, so what the portal gives back grows as they invest.
 */
export function PhotoSide({
  image,
  caption
}: PhotoSideProps) {
  return <div className="relative h-full w-full overflow-hidden bg-ink" data-id="element-409" data-mp="2j3qhvqy42m6g">
      <AnimatePresence initial={false} data-id="element-410" data-mp="2nzfd5i33lgwy">
        <motion.img key={image.src} src={image.src} alt={image.alt} initial={{
        opacity: 0,
        scale: 1.03
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="absolute inset-0 h-full w-full object-cover" data-id="element-411" data-mp="2o2l3t1rdkqus" />
      </AnimatePresence>

      <div aria-hidden="true" className="absolute inset-0 bg-ink opacity-60" data-id="element-412" data-mp="20hy3a21pztwu" />

      <div className="relative flex h-full flex-col justify-between p-6 sm:p-8" data-id="element-413" data-mp="1vhadtkt53l4q">
        <div className="flex items-center gap-3" data-id="element-414" data-mp="1qiqp5cn5vv8o">
          <span className="text-heading font-semibold tracking-tight text-on-ink" data-id="element-415" data-mp="1qk1uvi063d2f">Portal</span>
          <span aria-hidden="true" className="h-4 w-px bg-on-ink opacity-40" data-id="element-416" data-mp="2o7aaiwdhlrwi" />
          <span className="flex items-center gap-2 text-eyebrow-caption font-medium uppercase text-on-ink" data-id="element-417" data-mp="2j74p3xxt91mv">
            <span className="opacity-70" data-id="element-418" data-mp="2edjp60cnpxl1">Hosted by {WORDMARK}</span>
            <span className="sr-only" data-id="element-419" data-mp="29frx96c0ibgu">{WORDMARK}</span>
          </span>
        </div>

        <AnimatePresence mode="wait" data-id="element-420" data-mp="36w72uupbqac3">
          <motion.figure key={caption.line} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.28,
          ease: 'easeOut'
        }} className="flex max-w-md flex-col gap-3" data-id="element-421" data-mp="36x8hta9gngz3">
            <span className="flex items-center gap-2 text-eyebrow-caption font-semibold uppercase text-on-ink" data-id="element-422" data-mp="158uky2c0mqst">
              <span aria-hidden="true" className="h-px w-6 bg-highlight" data-id="element-423" data-mp="vgyjqiemukkd" />
              {caption.eyebrow}
            </span>

            <figcaption className="text-section-heading font-medium text-on-ink" data-id="element-424" data-mp="hgno722dohsv">
              {caption.line}
            </figcaption>

            {caption.names && caption.names.length > 0 ? <ul className="flex flex-wrap gap-2" data-id="element-425" data-mp="7jj55b3tx2ga">
                {caption.names.map(name => <li key={name} className="rounded-pill bg-on-ink px-3 py-1 text-small text-ink" data-id="element-426" data-mp="chat5u1cs4f7">
                    {name}
                  </li>)}
              </ul> : null}

            {caption.stat ? <p className="text-small text-on-ink opacity-80" data-id="element-427" data-mp="3rdmhsqcwlfpy">
                <span className="text-body font-semibold" data-id="element-428" data-mp="3rbtbjc67cxih">{caption.stat.value}</span>{' '}
                {caption.stat.label}
              </p> : null}

            {caption.footnote ? <p className="text-small italic text-on-ink opacity-80" data-id="element-429" data-mp="50edfb7yw4j1">{caption.footnote}</p> : null}
          </motion.figure>
        </AnimatePresence>
      </div>
    </div>;
}